import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiAcuerdosGestores } from 'api-rest';
import { switchMap } from 'rxjs/operators';
import { SnackbarService } from '../../services/snackbar.service';
import { SpinnerService } from '../../services/spinner.service';
import { ValidateService } from '../../services/validate.service';

@Component({
  selector: 'managers-agreements-generate-consent-modal',
  templateUrl: './managers-agreements-generate-consent-modal.component.html',
  styleUrls: ['./managers-agreements-generate-consent-modal.component.scss']
})
export class ManagersAgreementsGenerateConsentModalComponent implements OnInit {
  formGroup: FormGroup;
  consentimiento: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ManagersAgreementsGenerateConsentModalComponent>,
    private formBuilder: FormBuilder,
    public spinnerSrv: SpinnerService,
    private snackBarSrv: SnackbarService,
    private validateSrv: ValidateService,
    @Inject(ApiAcuerdosGestores.ConsentimientoControllerService)
    private consentimientoControllerSrv: ApiAcuerdosGestores.ConsentimientoControllerService,
  ) {
    this.formGroup = this.formBuilder.group({
      razonSocial: [{ value: null, disabled: false }, [Validators.required, Validators.maxLength(150)]],
      tipoDocumentoFirmante: [{ value: 'CIF', disabled: false }, [Validators.required]],
      codigoDocumentoFirmante: [{ value: null, disabled: false }, [Validators.required]],
      nombreFirmante: [{ value: null, disabled: false }, [Validators.required, Validators.maxLength(50)]],
      apellidosFirmante: [{ value: null, disabled: false }, [Validators.required, Validators.maxLength(100)]],
    });

    this.formGroup.get('tipoDocumentoFirmante')?.valueChanges.subscribe((tipoDoc: string) => {
      this.actualizarValidacionDocumento(tipoDoc);
    });

    this.formGroup.get('codigoDocumentoFirmante')?.valueChanges.subscribe((value: any) => {
      const normalized = (value ?? '').toString().toUpperCase();
      if (value !== normalized) {
        this.formGroup.get('codigoDocumentoFirmante')?.setValue(normalized, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.consentimiento = this.data?.consentimiento;

    this.formGroup.patchValue({
      razonSocial: this.consentimiento?.razonSocial ?? this.consentimiento?.poseedor?.razonSocial ?? null,
      tipoDocumentoFirmante: this.consentimiento?.tipoDocumentoFirmante === 'NIF' ? 'CIF' : (this.consentimiento?.tipoDocumentoFirmante ?? 'CIF'),
      codigoDocumentoFirmante: this.consentimiento?.codigoDocumentoFirmante ?? null,
      nombreFirmante: this.consentimiento?.nombreFirmante ?? null,
      apellidosFirmante: this.consentimiento?.apellidosFirmante ?? null,
    }, { emitEvent: false });

    this.actualizarValidacionDocumento(this.formGroup.get('tipoDocumentoFirmante')?.value);
  }

  private actualizarValidacionDocumento(tipoDoc: string): void {
    const control = this.formGroup.get('codigoDocumentoFirmante');
    control?.clearValidators();

    control?.setValidators([Validators.required, this.documentoFirmanteValidator()]);

    control?.updateValueAndValidity();
    control?.markAsTouched();
  }

  private documentoFirmanteValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? '').toString().trim().toUpperCase();
      if (!value) {
        return null;
      }

      const tipo = this.formGroup?.get('tipoDocumentoFirmante')?.value;
      if (tipo === 'DNI') {
        return this.isDniValid(value) ? null : { invalidDocument: true };
      }
      if (tipo === 'NIE') {
        return this.isNieValid(value) ? null : { invalidDocument: true };
      }
      if (tipo === 'CIF') {
        return this.isCifValid(value) ? null : { invalidDocument: true };
      }

      return { invalidDocument: true };
    };
  }

  private isDniValid(documento: string): boolean {
    if (!/^\d{8}[A-Z]$/.test(documento)) {
      return false;
    }
    const letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
    const number = Number.parseInt(documento.substring(0, 8), 10);
    const expected = letters[number % 23];
    return documento.charAt(8) === expected;
  }

  private isNieValid(documento: string): boolean {
    if (!/^[XYZ]\d{7}[A-Z]$/.test(documento)) {
      return false;
    }
    const prefixMap: { [key: string]: string } = { X: '0', Y: '1', Z: '2' };
    const mapped = prefixMap[documento.charAt(0)] + documento.substring(1, 8);
    const dniEquivalent = mapped + documento.charAt(8);
    return this.isDniValid(dniEquivalent);
  }

  private isCifValid(documento: string): boolean {
    if (!/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-Z]$/.test(documento)) {
      return false;
    }

    const controlChar = documento.charAt(8);
    const letter = documento.charAt(0);
    const digits = documento.substring(1, 8);

    let sumEven = 0;
    let sumOdd = 0;

    for (let i = 0; i < digits.length; i++) {
      const n = Number.parseInt(digits.charAt(i), 10);
      if ((i + 1) % 2 === 0) {
        sumEven += n;
      } else {
        const doubled = n * 2;
        sumOdd += Math.floor(doubled / 10) + (doubled % 10);
      }
    }

    const total = sumEven + sumOdd;
    const controlDigit = (10 - (total % 10)) % 10;
    const controlLetter = 'JABCDEFGHI'.charAt(controlDigit);

    if ('ABEH'.includes(letter)) {
      return controlChar === controlDigit.toString();
    }

    if ('PQRSNW'.includes(letter)) {
      return controlChar === controlLetter;
    }

    return controlChar === controlDigit.toString() || controlChar === controlLetter;
  }

  controlHasError(controlName: string, errorName: string): boolean {
    const control = this.formGroup.get(controlName);
    return !!(control?.hasError(errorName) && control?.touched);
  }

  cerrar(): void {
    this.dialogRef.close();
  }

  guardar(): void {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      this.snackBarSrv.showSnackBar('Debe completar los campos obligatorios', 'error');
      return;
    }

    if (!this.consentimiento?.id) {
      this.snackBarSrv.showSnackBar('No se pudo identificar el consentimiento', 'error');
      return;
    }

    const payload: ApiAcuerdosGestores.ConsentimientoDTO = {
      ...this.consentimiento,
      razonSocial: this.formGroup.get('razonSocial')?.value,
      tipoDocumentoFirmante: this.formGroup.get('tipoDocumentoFirmante')?.value === 'CIF' ? 'NIF' : this.formGroup.get('tipoDocumentoFirmante')?.value,
      codigoDocumentoFirmante: this.formGroup.get('codigoDocumentoFirmante')?.value?.toString().trim().toUpperCase(),
      nombreFirmante: this.formGroup.get('nombreFirmante')?.value,
      apellidosFirmante: this.formGroup.get('apellidosFirmante')?.value,
    } as any;

    this.spinnerSrv.loadingShow();
    this.consentimientoControllerSrv.updateById1(this.consentimiento.id, payload).pipe(
      switchMap(() => this.consentimientoControllerSrv.generarConsentimiento(this.consentimiento.id))
    ).subscribe({
      next: () => {
        this.spinnerSrv.loadingHide();
        this.snackBarSrv.showSnackBar('Consentimiento generado correctamente', 'success');
        this.dialogRef.close({ saved: true });
      },
      error: () => {
        this.spinnerSrv.loadingHide();
        this.snackBarSrv.showSnackBar('Ha ocurrido un error al generar el consentimiento', 'error');
      }
    });
  }
}
